/** Pure helpers for OrderNotificationsPanel (kept out of the .tsx for react-refresh). */
import type {
  ConfirmationDrift,
  ConfirmationDriftLine,
  NotificationKind,
  OrderNotificationEvent,
  SalesSiteSend,
  SendNowKind,
} from '../../types/orderNotifications';

export const KIND_LABEL: Record<NotificationKind, string> = {
  confirmation: 'Order confirmation',
  invoice_copy: 'Invoice copy',
  confirmation_update: 'Confirmation update',
};

export const TRIGGER_LABEL: Record<OrderNotificationEvent['trigger'], string> = {
  submit: 'on Sage submit',
  invoice_sweep: 'after invoice sync',
  sales_site_relay: "relayed Adam's email",
  manual: 'sent manually',
};

export function hasSent(events: OrderNotificationEvent[], kind: NotificationKind): boolean {
  return events.some((e) => e.kind === kind && e.status === 'sent');
}

export function formatWhen(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

/** Label for the send button given history + in-flight state. */
export function sendButtonLabel(
  kind: SendNowKind,
  events: OrderNotificationEvent[],
  sending: NotificationKind | null,
): string {
  if (sending === kind) return 'Sending…';
  const noun = kind === 'confirmation' ? 'confirmation' : 'invoice copy';
  return hasSent(events, kind) ? `Resend ${noun}` : `Send ${noun} now`;
}

function shortDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : `${d.getMonth() + 1}/${d.getDate()}`;
}

/**
 * Collapsed one-liner: latest outcome per kind for the portal's own sends, then
 * what the sales site mailed, e.g.
 * "Confirmation sent 8/26 · Invoice copy sent 8/27 · Sales site mailed 5 (9/17)".
 */
export function summaryLine(
  events: OrderNotificationEvent[],
  contactsCount: number,
  salesSiteSends: SalesSiteSend[] = [],
): string {
  const parts: string[] = [];
  const SUMMARY_LABEL: Record<NotificationKind, string> = {
    confirmation: 'Confirmation',
    invoice_copy: 'Invoice copy',
    confirmation_update: 'Confirmation update',
  };
  for (const kind of ['confirmation', 'confirmation_update', 'invoice_copy'] as NotificationKind[]) {
    const sent = events.find((e) => e.kind === kind && e.status === 'sent');
    const latest = events.find((e) => e.kind === kind);
    const label = SUMMARY_LABEL[kind];
    if (sent) parts.push(`${label} sent ${shortDate(sent.sent_at ?? sent.created_at)}`);
    else if (latest?.status === 'failed') parts.push(`${label} failed`);
    else if (latest?.status === 'skipped') parts.push(`${label} skipped`);
  }
  const salesSite = salesSiteSummary(salesSiteSends);
  if (salesSite) parts.push(salesSite);
  if (parts.length > 0) return `Buyer notifications: ${parts.join(' · ')}`;
  return contactsCount === 0
    ? 'Buyer notifications: no buyer picked'
    : 'Buyer notifications: nothing sent yet';
}

/** "Sales site mailed 5 (9/17)" / "Sales site mailed nobody (9/17)" / '' when it sent nothing yet. */
export function salesSiteSummary(sends: SalesSiteSend[]): string {
  if (sends.length === 0) return '';
  const addresses = new Set(sends.flatMap((s) => s.recipients));
  const stamps = sends
    .map((s) => s.sent_at)
    .filter((t): t is string => Boolean(t))
    .sort();
  const latest = stamps.length > 0 ? stamps[stamps.length - 1] : null;
  const when = latest ? ` (${shortDate(latest)})` : '';
  return addresses.size === 0
    ? `Sales site mailed nobody${when}`
    : `Sales site mailed ${addresses.size}${when}`;
}

export const SALES_SITE_KIND_LABEL: Record<SalesSiteSend['kind'], string> = {
  confirmation: 'Order confirmation',
  invoice: 'Invoice',
};

/** The orders@ copy that proves a sales-site row (owner ask: an evidence line naming the source email). */
export function salesSiteEvidence(send: SalesSiteSend): string {
  const copies = send.copy_count === 1 ? '1 copy' : `${send.copy_count} copies`;
  const from = send.source_from ? ` from ${send.source_from}` : '';
  return `orders@ copy: "${send.subject}"${from} · ${copies} in the mailbox`;
}

/** One row of the merged timeline: either the portal's own attempt or a sales-site send. */
export type TimelineEntry =
  | { source: 'portal'; at: string; key: string; event: OrderNotificationEvent }
  | { source: 'sales_site'; at: string; key: string; send: SalesSiteSend };

/**
 * Both senders in one list, newest first (the portal's list_events order).
 * A sales-site send with no timestamp sorts last — it happened, we just
 * cannot say when.
 */
export function mergeTimeline(
  events: OrderNotificationEvent[],
  sends: SalesSiteSend[],
): TimelineEntry[] {
  const rows: TimelineEntry[] = [
    ...events.map((event): TimelineEntry => ({
      source: 'portal',
      at: event.sent_at ?? event.created_at,
      key: `portal-${event.id}`,
      event,
    })),
    ...sends.map((send, i): TimelineEntry => ({
      source: 'sales_site',
      at: send.sent_at ?? '',
      key: `sales-site-${send.kind}-${send.invoice_no ?? ''}-${send.source_email_id ?? i}`,
      send,
    })),
  ];
  const ms = (iso: string) => {
    if (!iso) return Number.NEGATIVE_INFINITY;
    const t = new Date(iso).getTime();
    return Number.isNaN(t) ? Number.NEGATIVE_INFINITY : t;
  };
  return rows.sort((a, b) => ms(b.at) - ms(a.at));
}

/** "$89.00" from the API's decimal string; '' for null. */
export function money(value: string | null): string {
  if (value === null || value === undefined || value === '') return '';
  const n = Number(value);
  if (!Number.isFinite(n)) return value;
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const CHARGE_LABEL: Record<ConfirmationDriftLine['charge'], string> = {
  surcharge: 'Surcharge',
  freight: 'Freight',
};

/**
 * One line of the drift banner, e.g.
 *   "Surcharge: confirmation showed $89.00 · will bill $0.00 (KAMPS01 is exempt … since 9/10)"
 *   "Freight: confirmation showed TBD · will bill $0.00 (freight was waived in the portal)"
 */
export function driftLineText(line: ConfirmationDriftLine): string {
  const expected = money(line.expected) || '$0.00';
  const willBill = Number(line.expected) === 0 ? 'will not bill it' : `will bill ${expected}`;
  const since = line.since ? ` since ${shortDate(line.since)}` : '';
  return `${CHARGE_LABEL[line.charge]}: confirmation showed ${line.shown_display} · ${willBill} (${line.detail}${since})`;
}

/**
 * The banner headline. Names the email the buyer holds and when, e.g.
 *   "The confirmation the buyer holds (sales site, 9/9) shows charges this order will not bill."
 */
export function driftHeadline(drift: ConfirmationDrift): string {
  const who = drift.told_by === 'sales_site' ? "Adam's sales site" : 'the portal';
  const when = drift.told_at ? `, ${shortDate(drift.told_at)}` : '';
  const n = drift.lines.length;
  const what = n === 1 ? 'a charge' : 'charges';
  return `The confirmation the buyer holds (sent by ${who}${when}) shows ${what} this order will not bill as stated.`;
}

/** True when the portal's update went out after the email the drift was measured against. */
export function updateAlreadySent(drift: ConfirmationDrift): boolean {
  if (!drift.update_sent_at) return false;
  if (!drift.told_at) return true;
  return new Date(drift.update_sent_at).getTime() >= new Date(drift.told_at).getTime();
}
